import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const EUR_FORMAT = new Intl.NumberFormat("fr-FR", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

const DATE_FORMAT = new Intl.DateTimeFormat("fr-FR", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

export default class TvaRegimeComparatorController extends BaseController {
  static targets = [
    "errorBanner", "errorMessage",
    "franchiseBanner",
    "projectionHint",
    "vatCollectedCa12", "vatCollectedCa3",
    "vatDeductibleCa12", "vatDeductibleCa3",
    "vatNetCa12", "vatNetCa3",
    "regimeRadio",
    "periodicitySection",
    "recommendation",
    "periodicityRadio",
    "recommendationPillMonthly", "recommendationPillQuarterly",
    "deadlineHint",
    "submitBtn", "successMsg",
  ];

  static values = {
    loadEndpoint: { type: String, default: "/tva/choix-regime" },
    submitEndpoint: { type: String, default: "/tva/choix-regime" },
    submitMethod: { type: String, default: "POST" },
  };

  async connect() {
    this._isSubmitting = false;
    this._recommendation = null;
    this._deadlineDate = null;

    try {
      const data = await api.get(this.loadEndpointValue);
      this._applyInitialState(data);
    } catch (error) {
      const message = error instanceof APIError
        ? error.message
        : "Impossible de charger l'état du choix de régime de TVA.";
      this._showErrorBanner(message);
    }
  }

  _applyInitialState(data) {
    this._recommendation = data.recommendation ?? null;
    this._deadlineDate = data.deadlineDate ?? null;

    const isFranchise = data.subjectionStatus === "franchise";
    if (this.hasFranchiseBannerTarget) {
      this.franchiseBannerTarget.hidden = !isFranchise;
    }

    this._renderProjection(data.projection ?? null);
    this._renderRecommendationPill(this._recommendation?.periodicity);
    this._renderDeadlineHint(this._deadlineDate);
    this._preselect(data.currentRegime, data.currentPeriodicity);
  }

  _renderProjection(projection) {
    if (!projection) return;

    const collected = projection.vatCollectedEur ?? 0;
    const deductible = projection.vatDeductibleEur ?? 0;
    const net = projection.vatNetEur ?? (collected - deductible);

    this._setAmount("vatCollectedCa12", collected);
    this._setAmount("vatCollectedCa3", collected);
    this._setAmount("vatDeductibleCa12", deductible);
    this._setAmount("vatDeductibleCa3", deductible);
    this._setAmount("vatNetCa12", net, true);
    this._setAmount("vatNetCa3", net, true);

    if (this.hasProjectionHintTarget) {
      const annualCa = projection.annualCaEur ?? 0;
      this.projectionHintTarget.textContent =
        `Simulation basée sur votre projection de CA annuel (${EUR_FORMAT.format(annualCa)} HT).`;
    }
  }

  _renderRecommendationPill(periodicity) {
    if (this.hasRecommendationPillMonthlyTarget) {
      this.recommendationPillMonthlyTarget.hidden = periodicity !== "monthly";
    }
    if (this.hasRecommendationPillQuarterlyTarget) {
      this.recommendationPillQuarterlyTarget.hidden = periodicity !== "quarterly";
    }
  }

  _renderDeadlineHint(deadlineDate) {
    if (!this.hasDeadlineHintTarget) return;
    if (!deadlineDate) {
      this.deadlineHintTarget.textContent =
        "— Choix réversible à tout moment";
      return;
    }
    const date = new Date(deadlineDate + "T00:00:00");
    this.deadlineHintTarget.textContent =
      `— Vous pouvez modifier votre choix jusqu'au ${DATE_FORMAT.format(date)}`;
  }

  _preselect(currentRegime, currentPeriodicity) {
    if (!currentRegime) {
      this.periodicitySectionTarget.hidden = true;
      return;
    }
    for (const radio of this.regimeRadioTargets) {
      if (radio.value === currentRegime) {
        radio.checked = true;
      }
    }
    const isCa3 = currentRegime === "normal_ca3";
    this.periodicitySectionTarget.hidden = !isCa3;
    if (isCa3 && currentPeriodicity) {
      for (const radio of this.periodicityRadioTargets) {
        if (radio.value === currentPeriodicity) {
          radio.checked = true;
        }
      }
    }
  }

  onRegimeChange() {
    const regime = this._selectedRegime();
    const isCa3 = regime === "normal_ca3";
    this.periodicitySectionTarget.hidden = !isCa3;
    if (!isCa3) {
      for (const radio of this.periodicityRadioTargets) {
        radio.checked = false;
      }
    }
    this._hideErrorBanner();
    this._hideSuccess();
  }

  onPeriodicityChange() {
    this._hideErrorBanner();
    this._hideSuccess();
  }

  async submit(event) {
    if (event && typeof event.preventDefault === "function") event.preventDefault();
    if (this._isSubmitting) return;

    const regime = this._selectedRegime();
    if (!regime) {
      this._showErrorBanner("Sélectionnez un régime (CA12 ou CA3) avant de valider.");
      return;
    }

    const payload = { regime };
    if (regime === "normal_ca3") {
      const periodicity = this._selectedPeriodicity();
      if (!periodicity) {
        this._showErrorBanner("Sélectionnez une périodicité (mensuelle ou trimestrielle) pour le régime CA3.");
        return;
      }
      payload.periodicity = periodicity;
    }

    this._isSubmitting = true;
    this.submitBtnTarget.disabled = true;
    this.submitBtnTarget.setAttribute("aria-busy", "true");
    this._hideErrorBanner();
    this._hideSuccess();

    try {
      const method = this.submitMethodValue.toLowerCase();
      const apiFn = api[method];
      if (typeof apiFn !== "function") {
        throw new Error(`Unsupported submit method "${this.submitMethodValue}" for tva-regime-comparator.`);
      }
      const response = await apiFn.call(api, this.submitEndpointValue, payload);
      this.successMsgTarget.hidden = false;
      this.dispatch("choice-submitted", { detail: { payload, response } });
    } catch (error) {
      this._handleSubmitError(error);
    } finally {
      this._isSubmitting = false;
      this.submitBtnTarget.disabled = false;
      this.submitBtnTarget.removeAttribute("aria-busy");
    }
  }

  _handleSubmitError(error) {
    if (!(error instanceof APIError)) {
      this._showErrorBanner("Une erreur réseau est survenue. Réessayez.");
      return;
    }
    if (error.status === 422) {
      const fields = error.data?.error?.details?.fields ?? [];
      const first = fields[0];
      this._showErrorBanner(first?.message ?? error.message);
      return;
    }
    this._showErrorBanner(error.message);
  }

  _selectedRegime() {
    for (const radio of this.regimeRadioTargets) {
      if (radio.checked) return radio.value;
    }
    return null;
  }

  _selectedPeriodicity() {
    for (const radio of this.periodicityRadioTargets) {
      if (radio.checked) return radio.value;
    }
    return null;
  }

  _setAmount(targetName, valueEur, bold = false) {
    const targetProp = `has${targetName.charAt(0).toUpperCase()}${targetName.slice(1)}Target`;
    if (!this[targetProp]) return;
    const el = this[`${targetName}Target`];
    const formatted = EUR_FORMAT.format(valueEur ?? 0);
    if (bold) {
      el.innerHTML = "";
      const strong = document.createElement("strong");
      strong.textContent = formatted;
      el.appendChild(strong);
    } else {
      el.textContent = formatted;
    }
  }

  _showErrorBanner(message) {
    if (!this.hasErrorBannerTarget) return;
    if (this.hasErrorMessageTarget) this.errorMessageTarget.textContent = message;
    this.errorBannerTarget.hidden = false;
  }

  _hideErrorBanner() {
    if (!this.hasErrorBannerTarget) return;
    this.errorBannerTarget.hidden = true;
  }

  _hideSuccess() {
    if (!this.hasSuccessMsgTarget) return;
    this.successMsgTarget.hidden = true;
  }
}
